import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Grid, Card, CardContent, Typography, Box, Divider,
    Button, Chip, Stepper, Step, StepLabel, Alert, Skeleton,
    Stack, Paper, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, TextField, Avatar, CircularProgress, Popover, IconButton,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DownloadIcon            from '@mui/icons-material/Download';
import HelpOutlineIcon         from '@mui/icons-material/HelpOutline';
import CheckCircleIcon         from '@mui/icons-material/CheckCircle';
import CancelIcon              from '@mui/icons-material/Cancel';
import VisibilityOffIcon       from '@mui/icons-material/VisibilityOff';
import PersonIcon              from '@mui/icons-material/Person';
import PhoneIcon               from '@mui/icons-material/Phone';
import AccessTimeIcon          from '@mui/icons-material/AccessTime';
import PaymentIcon             from '@mui/icons-material/Payment';
import EditOutlinedIcon        from '@mui/icons-material/EditOutlined';
import LocationOnIcon          from '@mui/icons-material/LocationOn';
import AddCircleOutlineIcon    from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SaveIcon                from '@mui/icons-material/Save';
import CalendarMonthIcon       from '@mui/icons-material/CalendarMonth';
import ElectricBikeIcon        from '@mui/icons-material/ElectricBike';
import NotesIcon               from '@mui/icons-material/Notes';
import axios      from '../api/axiosConfig';
import { useAuth }     from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatCurrency, convertCents, getExchangeRate } from '../utils/currency';
import MiniCalendar   from './MiniCalendar';
import RatingWidget   from './RatingWidget';

const TERRACOTTA = '#C4622D';

const CURRENCIES = [
    { code: 'EUR', symbol: '€',  name: 'Euro'             },
    { code: 'USD', symbol: '$',  name: 'US Dollar'        },
    { code: 'AMD', symbol: '֏',  name: 'Armenian Dram'    },
    { code: 'GBP', symbol: '£',  name: 'British Pound'    },
    { code: 'RUB', symbol: '₽',  name: 'Russian Ruble'    },
    { code: 'JPY', symbol: '¥',  name: 'Japanese Yen'     },
    { code: 'CNY', symbol: '¥',  name: 'Chinese Yuan'     },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar'},
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar'  },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc'      },
];

const formatTime = (t) => {
    if (!t) return null;
    try { return t.toString().substring(0, 5); } catch { return t; }
};

// JS getDay(): 0=Sun…6=Sat  →  bitmask bit: 0=Mon…6=Sun
const JS_TO_BIT = [6, 0, 1, 2, 3, 4, 5];

// All six person categories — always shown in the pricing editor
const ALL_CATEGORIES = [
    'LOCAL ADULT', 'LOCAL CHILD', 'LOCAL INFANT',
    'FOREIGN ADULT', 'FOREIGN CHILD', 'FOREIGN INFANT',
];

// All five rental bike types — always shown in the rental pricing table
const ALL_BIKE_TYPES = [
    { key: 'NORMAL',   label: 'Normal' },
    { key: 'ELECTRIC', label: 'Electric' },
    { key: 'ROAD',     label: 'Road Bike' },
    { key: 'MOUNTAIN', label: 'Mountain Bike' },
    { key: 'GRAVEL',   label: 'Gravel Bike' },
];

// Addon metadata for display
const ADDON_META = {
    HELMET:   { label: 'Helmet' },
    LOCK:     { label: 'Lock' },
    KID_SEAT: { label: 'Kid Seat' },
    BAG:      { label: 'Bag' },
};

// Madrid timezone ISO builder (matches RentalBookingPage logic)
function buildMadridISO(date, timeStr) {
    const year    = date.getFullYear();
    const dateStr = [year, String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
    const lastSunMar = new Date(Date.UTC(year, 2, 31 - new Date(Date.UTC(year, 2, 31)).getUTCDay()));
    const lastSunOct = new Date(Date.UTC(year, 9, 31 - new Date(Date.UTC(year, 9, 31)).getUTCDay()));
    const noon   = new Date(`${dateStr}T12:00:00Z`).getTime();
    const offset = noon >= lastSunMar.getTime() && noon < lastSunOct.getTime() ? '+02:00' : '+01:00';
    return `${dateStr}T${timeStr}:00${offset}`;
}

function toISODate(d) {
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function BookingDetailsSkeleton() {
    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Skeleton variant="rounded" width="100%" height={52} sx={{ borderRadius: '12px', mb: 4 }} />
            <Grid container spacing={3} alignItems="flex-start">
                {[1, 2, 3].map(i => (
                    <Grid key={i} item xs={12} md={4}>
                        <Card elevation={2}>
                            <CardContent sx={{ p: 3 }}>
                                <Skeleton variant="text" width="70%" height={28} sx={{ mb: 2 }} />
                                <Stack spacing={1.5}>
                                    {Array.from({ length: 4 }).map((_, j) => (
                                        <Skeleton key={j} variant="text" width="100%" height={18} />
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────
const BookingDetailsContent = () => {
    const { id }           = useParams();
    const [searchParams]   = useSearchParams();
    const navigate         = useNavigate();
    const { user }         = useAuth();
    const { t }            = useTranslation();
    const { currency: navCurrency, rates, format } = useCurrency();

    const isSuccess  = searchParams.get('success')  === 'true';
    const isReserved = searchParams.get('reserved') === 'true';
    const isManager = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    // ── Core state ────────────────────────────────────────────────────────────
    const [booking,       setBooking]       = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [rawBill,       setRawBill]       = useState(null);
    const [refundDialog,  setRefundDialog]  = useState({ open: false, action: null });
    const [refundNote,    setRefundNote]    = useState('');
    const [refundLoading, setRefundLoading] = useState(false);
    const [cancelDialog,  setCancelDialog]  = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [hideDialog,    setHideDialog]    = useState(false);
    const [hideLoading,   setHideLoading]   = useState(false);

    // ── Payment state ─────────────────────────────────────────────────────────
    const [payLoading, setPayLoading] = useState(false);

    // ── Edit state ────────────────────────────────────────────────────────────
    const [offerSlots,       setOfferSlots]       = useState([]);
    const [editCounts,       setEditCounts]       = useState({});
    const [editEBikeCounts,  setEditEBikeCounts]  = useState({});
    const [electricBikeEdit, setElectricBikeEdit] = useState({ quantity: 0, itemId: null, unitPrice: 0 });
    const [rentalEditCounts, setRentalEditCounts] = useState({});
    const [addonConfigs,     setAddonConfigs]     = useState([]);
    const [addonEditCounts,  setAddonEditCounts]  = useState({});
    const [savingAddons,     setSavingAddons]     = useState(false);
    const [rentalInfo,       setRentalInfo]       = useState(null);  // { id, durationType, days }
    const [rentalDateOpen,   setRentalDateOpen]   = useState(false);
    const [rentalEditDate,   setRentalEditDate]   = useState('');
    const [rentalEditTime,   setRentalEditTime]   = useState('');
    const [rentalEditHours,  setRentalEditHours]  = useState(1);
    const [savingRentalDate, setSavingRentalDate] = useState(false);
    const [savingCounts,     setSavingCounts]     = useState(false);
    const [savingRental,     setSavingRental]     = useState(false);
    const [editNotes,        setEditNotes]        = useState('');
    const [savingNotes,      setSavingNotes]      = useState(false);
    const [calendarAnchor, setCalendarAnchor] = useState(null);
    const [editDate,       setEditDate]       = useState(null);
    const [editSlot,       setEditSlot]       = useState(null);
    const [savingDate,     setSavingDate]     = useState(false);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchBookingDetails = useCallback(async () => {
        try {
            const [billRes, itemsRes] = await Promise.all([
                axios.get(`/bills/view/id/${id}`),
                axios.get(`/bill_items/view/bill-id/${id}`)
            ]);
            const bill  = billRes.data;
            const items = itemsRes.data;
            setRawBill(bill);
            setEditNotes(bill.notes || '');

            const sourceType = bill.billType === 'BIKE_TRAVEL' ? 'BIKE_TRAVEL'
                             : bill.billType === 'RENTAL'     ? 'RENTAL'
                             : (bill.offerType || 'TOUR');
            const sourceId   = bill.offerId;
            const createdAt  = bill.created_at || bill.createdAt;
            const status     = bill.billStatus;

            let offer = {}, rentalData = {};

            if (sourceType === 'RENTAL') {
                try {
                    const r = await axios.get(`/rentals/view/bill/${bill.id}`);
                    rentalData = r.data;
                    setRentalInfo({ id: rentalData.id, durationType: rentalData.durationType, days: rentalData.days });
                    // Pre-fill the date/time edit fields from existing rental
                    if (rentalData.startTime) {
                        const st = new Date(rentalData.startTime);
                        setRentalEditDate(st.toISOString().slice(0, 10));
                        setRentalEditTime(st.toTimeString().slice(0, 5));
                        if (rentalData.durationType === 'HOURLY' && rentalData.days) {
                            setRentalEditHours(rentalData.days);
                        }
                    }
                } catch { /* silent */ }

                // Fetch addon configs and rental prices in parallel
                let addonConfigsList = [], rentalPricesList = [];
                try {
                    const [acRes, rpRes] = await Promise.all([
                        axios.get('/rentals/addon-prices'),
                        axios.get('/rentals/prices'),
                    ]);
                    addonConfigsList = acRes.data || [];
                    rentalPricesList = rpRes.data || [];
                } catch { /* silent */ }
                setAddonConfigs(addonConfigsList);

                // Build a price lookup from rental_prices (source of truth for unit prices)
                // Multiply by duration units (hours/days) to get per-bike total
                const units = rentalData.days || 1;
                const priceLookup = {};
                rentalPricesList.forEach(rp => {
                    if (rp.bikeType && rp.durationType === rentalData.durationType) {
                        priceLookup[rp.bikeType] = Number(rp.pricePerUnit || 0) * units;
                    }
                });

                // Group bike bill_items by type (one bill_item per physical bike, qty=1 each)
                const rentalCounts = {};
                ALL_BIKE_TYPES.forEach(({ key }) => {
                    rentalCounts[key] = { quantity: 0, unitPrice: priceLookup[key] || 0, itemIds: [] };
                });
                const addonCounts = {};
                addonConfigsList.forEach(ac => {
                    addonCounts[ac.equipmentType] = { qty: 0, unitPrice: Number(ac.pricePerUnit || 0), itemId: null };
                });

                (items || []).filter(i => !i.billing || i.billing === 'BASE').forEach(item => {
                    const desc = item.description || '';
                    if (desc.includes(' bicycle')) {
                        const bt = desc.split(' bicycle')[0];
                        if (rentalCounts[bt] !== undefined) {
                            rentalCounts[bt].quantity += 1;
                            // Use bill item price if available, otherwise keep the lookup price
                            if (Number(item.unitPrice || item.unit_price || 0) > 0) {
                                rentalCounts[bt].unitPrice = Number(item.unitPrice || item.unit_price || 0);
                            }
                            rentalCounts[bt].itemIds.push(item.id);
                        }
                    } else if (desc.endsWith(' add-on')) {
                        const et = desc.replace(' add-on', '').replace(/ /g, '_').toUpperCase();
                        addonCounts[et] = {
                            qty:       Number(item.quantity || 0),
                            unitPrice: Number(item.unitPrice || item.unit_price || 0) || (addonCounts[et]?.unitPrice || 0),
                            itemId:    item.id,
                        };
                    }
                });
                setRentalEditCounts(rentalCounts);
                setAddonEditCounts(addonCounts);
            } else if (sourceType === 'BIKE_TRAVEL') {
                if (sourceId) {
                    try {
                        const offerRes = await axios.get(`/api/bike-travel-offers/view/${sourceId}`);
                        offer = offerRes.data;
                    } catch { /* silent */ }
                }
            } else {
                if (sourceId) {
                    try {
                        const offerRes = await axios.get(`/api/tourOffers/view/${sourceId}`);
                        offer = offerRes.data;
                    } catch { /* silent */ }
                }
                setOfferSlots(offer.slots || []);

                // Build price map and editCounts (TOUR only)
                const priceMap = {};
                (offer.prices || []).forEach(p => {
                    const key = `${p.isForeigner ? 'FOREIGN' : 'LOCAL'} ${p.category}`;
                    priceMap[key] = p.price;
                });
                const itemMap = {};
                (items || []).filter(i => !i.billing || i.billing === 'BASE').forEach(item => {
                    const clean = item.description?.includes('-')
                        ? item.description.split('-')[0].trim()
                        : item.description;
                    itemMap[clean] = {
                        id:        item.id,
                        quantity:  item.quantity,
                        unitPrice: item.unitPrice || item.unit_price || 0,
                    };
                });
                const counts = {};
                ALL_CATEGORIES.forEach(key => {
                    const ex = itemMap[key];
                    counts[key] = {
                        quantity:  ex?.quantity  ?? 0,
                        itemId:    ex?.id        ?? null,
                        unitPrice: ex?.unitPrice ?? priceMap[key] ?? 0,
                    };
                });
                setEditCounts(counts);

                // Per-category e-bike counts (for BIKE tours 2-column edit)
                const eBikeCounts = {};
                ALL_CATEGORIES.forEach(key => {
                    const eBikeItem = (items || []).find(i =>
                        (i.description || '').split('-')[0].trim() === key + ' ELECTRIC'
                    );
                    eBikeCounts[key] = {
                        quantity:  eBikeItem?.quantity  ?? 0,
                        itemId:    eBikeItem?.id         ?? null,
                        unitPrice: eBikeItem?.unitPrice  ?? eBikeItem?.unit_price
                                   ?? Math.round((priceMap[key] || 0) * 1.30),
                    };
                });
                setEditEBikeCounts(eBikeCounts);

                // Electric bike upgrade item (BIKE tours — non-ELECTRIC suffix format)
                const eBikeRaw = (items || []).find(i => (i.description || '').includes('ELECTRIC_BIKE'));
                setElectricBikeEdit(eBikeRaw
                    ? { quantity: eBikeRaw.quantity, itemId: eBikeRaw.id, unitPrice: eBikeRaw.unitPrice || eBikeRaw.unit_price || 0 }
                    : { quantity: 0, itemId: null, unitPrice: 0 });
            }

            // ── Timeline ──────────────────────────────────────────────────────
            const tourDate   = (sourceType === 'RENTAL' || sourceType === 'BIKE_TRAVEL') ? null : (bill.tourDate || null);
            const enjoyLabel = sourceType === 'RENTAL' ? 'Rental Day' : 'Journey';
            const enjoyDate  = sourceType === 'RENTAL' ? rentalData.startTime
                             : sourceType === 'BIKE_TRAVEL' ? bill.startDate
                             : tourDate;
            const today      = new Date().toISOString().slice(0, 10);
            const isPast     = sourceType === 'RENTAL'
                ? (rentalData.startTime ? new Date(rentalData.startTime) < new Date() : false)
                : sourceType === 'BIKE_TRAVEL'
                    ? (bill.startDate ? today >= bill.startDate : false)
                    : (tourDate ? today >= tourDate : false);

            let timeline;
            if (sourceType === 'BIKE_TRAVEL' && status === 'RESERVED') {
                timeline = [
                    { label: 'Booking Created',  date: createdAt,      completed: true  },
                    { label: '30% Deposit Paid', date: createdAt,      completed: true  },
                    { label: 'Balance Payment',  date: null,           completed: false },
                    { label: 'Journey',          date: bill.startDate, completed: false },
                ];
            } else if (['CANCELED', 'DELETED'].includes(status)) {
                timeline = [
                    { label: 'Booking Created',  date: createdAt,                    completed: true },
                    { label: 'Booking Canceled', date: bill.canceledAt || createdAt, completed: true },
                ];
            } else if (['REFUND_REQUESTED', 'REFUNDED'].includes(status)) {
                timeline = [
                    { label: 'Booking Created',   date: createdAt,                           completed: true                  },
                    { label: 'Payment Completed', date: createdAt,                           completed: true                  },
                    { label: 'Refund Requested',  date: bill.refundRequestedAt || createdAt, completed: true                  },
                    { label: 'Refund Approved',   date: bill.refundedAt || null,             completed: status === 'REFUNDED' },
                ];
            } else if (status === 'PAID') {
                timeline = [
                    { label: 'Booking Created',   date: createdAt, completed: true  },
                    { label: 'Payment Completed', date: createdAt, completed: true  },
                    { label: 'Facturation',       date: createdAt, completed: true  },
                    { label: enjoyLabel,          date: enjoyDate, completed: isPast },
                ];
            } else {
                timeline = [
                    { label: 'Booking Created', date: createdAt, completed: true  },
                    { label: 'Payment',         date: null,      completed: false },
                    { label: 'Facturation',     date: null,      completed: false },
                    { label: enjoyLabel,        date: null,      completed: false },
                ];
            }

            const startTime = sourceType === 'RENTAL' ? null : formatTime(bill.slotStartTime);
            const endTime   = sourceType === 'RENTAL' ? null : formatTime(bill.slotEndTime);

            setBooking({
                id: bill.id,
                sourceType,
                sourceId,
                tourType:   offer.type || null,
                billStatus: bill.billStatus,
                currency:   bill.currency || 'USD',
                depositAmount: Number(bill.depositAmount) || 0,
                // Name + image (set for both types)
                tourName:  sourceType === 'RENTAL'
                    ? (rentalData.offerName || 'Bicycle Rental')
                    : (offer.title || 'Tour Information'),
                tourImage: sourceType === 'RENTAL'
                    ? (rentalData.offerImageUrl || 'https://images.unsplash.com/photo-1559564484-e48b3e040ff4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')
                    : (offer.imageUrl          || 'https://images.unsplash.com/photo-1559564484-e48b3e040ff4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'),
                // TOUR-specific
                tourDate,
                startTime,
                endTime,
                duration:     offer.duration   || null,
                meetingPoint: offer.startPoint || null,
                // RENTAL-specific
                rentalStartTime: rentalData.startTime || null,
                rentalEndTime:   rentalData.endTime   || null,
                districtAddress: rentalData.districtAddress || null,
                // Customer
                customerName:     bill.customerName     || null,
                customerEmail:    bill.customerEmail    || null,
                customerPhone:    bill.customerPhone    || null,
                customerImageUrl: bill.customerImageUrl || null,
                // Bill items
                billItems: items ? items.map(item => {
                    const cleanDesc = item.description?.includes('-')
                        ? item.description.split('-')[0].trim()
                        : item.description;
                    return {
                        id:         item.id,
                        category:   cleanDesc,
                        quantity:   item.quantity,
                        unitPrice:  item.unitPrice || item.unit_price || 0,
                        amount:     item.amount    || 0,
                        billing:    item.billing   || 'BASE',
                        refundable: item.refundable ?? true,
                    };
                }) : [],
                notes:      bill.notes       || '',
                subtotal:   bill.subtotal    || 0,
                tax:        (bill.totalAmount || 0) - (bill.subtotal || 0),
                totalPrice: bill.totalAmount || 0,
                createdDate: createdAt,
                timeline,
            });
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchBookingDetails(); }, [fetchBookingDetails]);

    // ── Confirm payment on success redirect (webhook fallback) ────────────────
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if ((!isSuccess && !isReserved) || !sessionId || !rawBill) return;
        if (rawBill.billStatus === 'PAID' || rawBill.billStatus === 'RESERVED') return;
        axios.post(`/bills/${id}/confirm-payment?sessionId=${sessionId}`)
            .then(() => fetchBookingDetails())
            .catch(() => { /* silent — status will remain as-is */ });
    }, [isSuccess, isReserved, rawBill, id, searchParams, fetchBookingDetails]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Pay handler — redirects to Stripe ────────────────────────────────────
    const handlePay = async () => {
        setPayLoading(true);
        try {
            const isReservedBikeTravel = rawBill?.billType === 'BIKE_TRAVEL' && rawBill?.billStatus === 'RESERVED';
            const endpoint = isReservedBikeTravel
                ? `/api/bike-travel-offers/pay-balance/${id}`
                : `/bills/${id}/stripe-session`;
            const { data } = await axios.post(endpoint);
            window.location.href = data.stripeSessionUrl || data.sessionUrl;
        } catch (err) {
            setPayLoading(false);
            alert('Payment failed: ' + (err.response?.data?.message || err.message));
        }
    };

    // ── Status handlers ───────────────────────────────────────────────────────
    const handleStatusUpdate = async (newStatus) => {
        try {
            await axios.put(`/bills/edit/${id}`, { ...rawBill, billStatus: newStatus });
            await fetchBookingDetails();
        } catch (err) { alert('Update failed: ' + (err.response?.data?.message || err.message)); }
    };

    const handleCancel = async () => {
        setCancelLoading(true);
        try { await handleStatusUpdate('CANCELED'); setCancelDialog(false); }
        finally { setCancelLoading(false); }
    };

    const handleHide = async () => {
        setHideLoading(true);
        try { await handleStatusUpdate('DELETED'); setHideDialog(false); navigate('/my-bookings'); }
        finally { setHideLoading(false); }
    };

    const handleRefundDecision = async () => {
        setRefundLoading(true);
        try {
            await handleStatusUpdate(refundDialog.action === 'approve' ? 'REFUNDED' : 'PAID');
            setRefundDialog({ open: false, action: null });
            setRefundNote('');
        } finally { setRefundLoading(false); }
    };

    // ── People count handlers ─────────────────────────────────────────────────
    const handleCountChange = (key, delta) => {
        setEditCounts(prev => ({
            ...prev,
            [key]: { ...prev[key], quantity: Math.max(0, prev[key].quantity + delta) },
        }));
    };

    const handleEBikeCountChange = (key, delta) => {
        setEditEBikeCounts(prev => ({
            ...prev,
            [key]: { ...prev[key], quantity: Math.max(0, prev[key].quantity + delta) },
        }));
    };

    const handleRentalCountChange = (bikeType, delta) => {
        setRentalEditCounts(prev => ({
            ...prev,
            [bikeType]: { ...prev[bikeType], quantity: Math.max(0, prev[bikeType].quantity + delta) },
        }));
    };

    const handleSaveCounts = async () => {
        setSavingCounts(true);
        try {
            await Promise.all(
                ALL_CATEGORIES.map(async key => {
                    const { quantity, itemId, unitPrice } = editCounts[key];
                    const body = {
                        bill:        { id: booking.id },
                        quantity,
                        description: key,
                        unitPrice,
                        amount:      +(quantity * unitPrice).toFixed(2),
                        refundable:  true,
                        billing:     'BASE',
                    };
                    if (itemId && quantity === 0) {
                        await axios.delete(`/bill_items/${itemId}`);
                    } else if (itemId && quantity > 0) {
                        await axios.put(`/bill_items/edit/${itemId}`, body);
                    } else if (!itemId && quantity > 0) {
                        await axios.post('/bill_items/bill_item', body);
                    }
                })
            );

            // Save e-bike items: per-category (BIKE tour) or global upgrade (non-BIKE)
            let eBikeSubtotal = 0;
            if (booking.tourType === 'BIKE') {
                // Per-category " ELECTRIC" items
                await Promise.all(
                    ALL_CATEGORIES.map(async key => {
                        const { quantity: eQty, itemId: eId, unitPrice: ePrice } = editEBikeCounts[key] || {};
                        eBikeSubtotal += (eQty || 0) * (ePrice || 0);
                        const eBody = {
                            bill: { id: booking.id }, quantity: eQty || 0,
                            description: key + ' ELECTRIC',
                            unitPrice: ePrice || 0, amount: +((eQty || 0) * (ePrice || 0)).toFixed(2),
                            refundable: true, billing: 'BASE',
                        };
                        if (eId && (!eQty || eQty === 0)) {
                            await axios.delete(`/bill_items/${eId}`);
                        } else if (eId && eQty > 0) {
                            await axios.put(`/bill_items/edit/${eId}`, eBody);
                        } else if (!eId && eQty > 0) {
                            await axios.post('/bill_items/bill_item', eBody);
                        }
                    })
                );
            } else {
                // Global ELECTRIC_BIKE UPGRADE item
                const { quantity: eQty, itemId: eId, unitPrice: ePrice } = electricBikeEdit;
                eBikeSubtotal = (eQty || 0) * (ePrice || 0);
                if (eId || eQty > 0) {
                    const eBody = {
                        bill: { id: booking.id }, quantity: eQty,
                        description: 'ELECTRIC_BIKE UPGRADE',
                        unitPrice: ePrice, amount: +(eQty * ePrice).toFixed(2),
                        refundable: true, billing: 'BASE',
                    };
                    if (eId && eQty === 0) {
                        await axios.delete(`/bill_items/${eId}`);
                    } else if (eId && eQty > 0) {
                        await axios.put(`/bill_items/edit/${eId}`, eBody);
                    } else if (!eId && eQty > 0) {
                        await axios.post('/bill_items/bill_item', eBody);
                    }
                }
            }

            // Recalculate and persist bill totals
            const peopleSubtotal = ALL_CATEGORIES.reduce((sum, key) => {
                const { quantity, unitPrice } = editCounts[key];
                return sum + quantity * unitPrice;
            }, 0);
            const newSubtotal = peopleSubtotal + eBikeSubtotal;
            const taxTotal = (booking.billItems || [])
                .filter(i => i.billing === 'TAX')
                .reduce((sum, i) => sum + i.amount, 0);
            await axios.put(`/bills/edit/${id}`, {
                ...rawBill,
                subtotal:    +newSubtotal.toFixed(2),
                totalAmount: +(newSubtotal + taxTotal).toFixed(2),
            });
            await fetchBookingDetails();
        } catch (err) {
            alert('Save failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingCounts(false);
        }
    };

    // ── Rental counts save ────────────────────────────────────────────────────
    const handleSaveRentalCounts = async () => {
        setSavingRental(true);
        try {
            await Promise.all(
                ALL_BIKE_TYPES.map(async ({ key }) => {
                    const { quantity: newQty, unitPrice, itemIds = [] } = rentalEditCounts[key] || {};
                    const oldQty = itemIds.length;
                    // Delete excess items
                    if (newQty < oldQty) {
                        await Promise.all(itemIds.slice(newQty).map(iid => axios.delete(`/bill_items/${iid}`)));
                    }
                    // Create new items (one per additional physical bike, qty=1 each)
                    if (newQty > oldQty) {
                        // Reuse the description from an existing bill_item for this bike type (preserves "— 2h" suffix)
                        const existingDesc = (booking.billItems || []).find(bi =>
                            (bi.category || '').includes(key + ' bicycle') || (bi.description || '').startsWith(key + ' bicycle')
                        )?.category || key + ' bicycle';
                        for (let i = 0; i < newQty - oldQty; i++) {
                            await axios.post('/bill_items/bill_item', {
                                bill: { id: booking.id }, quantity: 1,
                                description: existingDesc, unitPrice, amount: unitPrice,
                                refundable: false, billing: 'BASE',
                            });
                        }
                    }
                })
            );
            const bikeSubtotal = ALL_BIKE_TYPES.reduce((s, { key }) =>
                s + (rentalEditCounts[key]?.quantity || 0) * (rentalEditCounts[key]?.unitPrice || 0), 0);
            const addonSubtotal = Object.values(addonEditCounts).reduce((s, { qty, unitPrice }) =>
                s + (qty || 0) * (unitPrice || 0), 0);
            const newSubtotal = bikeSubtotal + addonSubtotal;
            await axios.put(`/bills/edit/${id}`, {
                ...rawBill,
                subtotal:    +newSubtotal.toFixed(2),
                totalAmount: +newSubtotal.toFixed(2),
            });
            await fetchBookingDetails();
        } catch (err) {
            alert('Save failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingRental(false);
        }
    };

    // ── Addon handlers ────────────────────────────────────────────────────────
    const totalBikeCount = ALL_BIKE_TYPES.reduce((sum, { key }) => sum + (rentalEditCounts[key]?.quantity || 0), 0);

    const handleAddonCountChange = (equipmentType, delta) => {
        setAddonEditCounts(prev => {
            const newQty = Math.max(0, (prev[equipmentType]?.qty || 0) + delta);
            return {
                ...prev,
                [equipmentType]: {
                    ...prev[equipmentType],
                    qty: Math.min(newQty, totalBikeCount),
                },
            };
        });
    };

    const handleSaveAddons = async () => {
        if (!rentalInfo?.id) return;
        setSavingAddons(true);
        try {
            await Promise.all(
                Object.entries(addonEditCounts).map(([et, { qty }]) =>
                    axios.put(`/rentals/${rentalInfo.id}/addons/${et}?quantity=${qty || 0}`)
                )
            );
            const bikeSubtotal = ALL_BIKE_TYPES.reduce((s, { key }) =>
                s + (rentalEditCounts[key]?.quantity || 0) * (rentalEditCounts[key]?.unitPrice || 0), 0);
            const addonSubtotal = Object.values(addonEditCounts).reduce((s, { qty, unitPrice }) =>
                s + (qty || 0) * (unitPrice || 0), 0);
            const newSubtotal = bikeSubtotal + addonSubtotal;
            await axios.put(`/bills/edit/${id}`, {
                ...rawBill,
                subtotal:    +newSubtotal.toFixed(2),
                totalAmount: +newSubtotal.toFixed(2),
            });
            await fetchBookingDetails();
        } catch (err) {
            alert('Save failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingAddons(false);
        }
    };

    // ── Rental date/time handlers ─────────────────────────────────────────────
    const handleSaveRentalDate = async () => {
        if (!rentalEditDate || !rentalEditTime) return;
        setSavingRentalDate(true);
        try {
            const startISO = buildMadridISO(new Date(rentalEditDate + 'T00:00:00'), rentalEditTime);
            const startDt  = new Date(startISO);
            let endDt;
            if (rentalInfo?.durationType === 'HOURLY') {
                endDt = new Date(startDt.getTime() + rentalEditHours * 3600000);
            } else {
                const days = rentalInfo?.days || 1;
                endDt = new Date(startDt.getTime() + days * 86400000);
            }
            const endISO = endDt.toISOString().replace('Z', '+00:00');
            await axios.put(`/rentals/edit/bill/${id}/datetime`, {
                startTime: startISO,
                endTime:   endISO,
            });
            setRentalDateOpen(false);
            await fetchBookingDetails();
        } catch (err) {
            alert('Save failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingRentalDate(false);
        }
    };

    // ── Date / time handlers ──────────────────────────────────────────────────
    const handleOpenCalendar = (event) => {
        setCalendarAnchor(event.currentTarget);
        setEditDate(booking.tourDate ? new Date(booking.tourDate + 'T00:00:00') : null);
        setEditSlot(null);
    };

    const handleCloseCalendar = () => {
        setCalendarAnchor(null);
        setEditDate(null);
        setEditSlot(null);
    };

    const handleSaveDate = async () => {
        if (!editDate || !editSlot) return;
        setSavingDate(true);
        try {
            await axios.put(`/bills/edit/${id}`, {
                ...rawBill,
                tourDate:      toISODate(editDate),
                slotStartTime: editSlot.startTime,
                slotEndTime:   editSlot.endTime,
            });
            handleCloseCalendar();
            await fetchBookingDetails();
        } catch (err) {
            alert('Save failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingDate(false);
        }
    };

    // ── Notes handler ─────────────────────────────────────────────────────────
    const handleSaveNotes = async () => {
        setSavingNotes(true);
        try {
            await axios.put(`/bills/edit/${id}`, { ...rawBill, notes: editNotes || null });
            await fetchBookingDetails();
        } catch (err) {
            alert('Save failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingNotes(false);
        }
    };

    // ── PDF Invoice handler ───────────────────────────────────────────────────
    const handleDownloadInvoice = async () => {
        try {
            const { jsPDF } = await import('jspdf');
            const QRCode    = await import('qrcode');

            // Color palette
            const TC  = [196, 98,  45 ];  // TERRACOTTA
            const WN  = [44,  31,  20 ];  // WALNUT
            const GD  = [201, 151, 58 ];  // GOLD
            const PC  = [247, 243, 238];  // PARCHMENT
            const GR  = [120, 120, 120];  // gray

            const doc = new jsPDF({ unit: 'mm', format: 'a4' });
            const PW  = 210; // page width
            const PH  = 297; // page height

            // ── Helper: load URL to base64 ─────────────────────────────────
            const fetchBase64 = async (url) => {
                try {
                    const res  = await fetch(url);
                    const blob = await res.blob();
                    return await new Promise((res, rej) => {
                        const rd = new FileReader();
                        rd.onload  = () => res(rd.result);
                        rd.onerror = rej;
                        rd.readAsDataURL(blob);
                    });
                } catch { return null; }
            };

            // Load images
            const carpetUrl  = 'https://machanents.com/media/product/RGSN0329-1.jpg';
            const rentalUrl  = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80';
            const offerImage = booking?.tourImage || null;

            const [carpetB64, offerB64] = await Promise.all([
                fetchBase64(carpetUrl),
                fetchBase64(booking?.sourceType === 'RENTAL' ? rentalUrl : (offerImage || rentalUrl)),
            ]);

            // ── 1. HERO STRIP ──────────────────────────────────────────────
            const heroH = 45;
            if (carpetB64) {
                doc.addImage(carpetB64, 'JPEG', 0, 0, PW, heroH);
            } else {
                doc.setFillColor(...WN);
                doc.rect(0, 0, PW, heroH, 'F');
            }
            // Dark overlay
            doc.setFillColor(26, 26, 46);
            doc.setGState(doc.GState({ opacity: 0.58 }));
            doc.rect(0, 0, PW, heroH, 'F');
            doc.setGState(doc.GState({ opacity: 1 }));

            // Brand name
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text('HayTour', 14, 18);

            // Subtitle
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...GD);
            doc.text('Booking Invoice', 14, 26);

            // Gold underline
            doc.setDrawColor(...GD);
            doc.setLineWidth(0.6);
            doc.line(14, 28, 80, 28);

            // Tagline
            doc.setFontSize(8);
            doc.setTextColor(200, 200, 200);
            doc.text('Sevi Tours  ·  Armenia  ·  haytour.es', 14, 34);

            // QR code (top-right of hero)
            const bookingUrl = `https://www.haytour.es/booking/${id}`;
            const qrDataUrl  = await QRCode.toDataURL(bookingUrl, { width: 120, margin: 1 });
            doc.addImage(qrDataUrl, 'PNG', PW - 36, 6, 26, 26);
            doc.setFontSize(6);
            doc.setTextColor(180, 180, 180);
            doc.text('Scan to view', PW - 35, 34.5);

            // ── 2. DETAILS + OFFER IMAGE ──────────────────────────────────
            const detailsY = heroH + 8;
            const leftX    = 14;
            const rightX   = 120;
            const imgW     = 72;
            const imgH     = 52;

            // LEFT: booking details
            doc.setTextColor(...WN);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            const offerName = booking?.tourName || rawBill?.offerTitle || 'HayTour Booking';
            const nameLines = doc.splitTextToSize(offerName, rightX - leftX - 8);
            doc.text(nameLines, leftX, detailsY);
            let ly = detailsY + nameLines.length * 7;

            // Status badge
            const statusText = rawBill?.billStatus || '';
            const badgeColors = {
                PAID: [74, 103, 65], RESERVED: [33, 150, 243],
                DRAFT: [245, 124, 0], CANCELED: [198, 40, 40],
            };
            const bc = badgeColors[statusText] || GR;
            doc.setFillColor(...bc);
            doc.roundedRect(leftX, ly, 34, 6, 1.5, 1.5, 'F');
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(statusText, leftX + 3, ly + 4.3);
            ly += 10;

            // Detail rows
            doc.setFontSize(9.5);
            const detailRows = [];
            const customerLabel = rawBill?.customerName || rawBill?.guestEmail || rawBill?.guestPhone || '—';
            detailRows.push(['Customer', customerLabel]);
            if (rawBill?.tourDate)        detailRows.push(['Tour Date',    rawBill.tourDate]);
            if (rawBill?.startDate)       detailRows.push(['Start Date',   rawBill.startDate]);
            if (rawBill?.participantCount) detailRows.push(['Participants', String(rawBill.participantCount)]);
            detailRows.push(['Reference', id.slice(0, 18) + '…']);

            for (const [label, value] of detailRows) {
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...GR);
                doc.text(label + ':', leftX, ly);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...WN);
                doc.text(value, leftX + 28, ly);
                ly += 7;
            }

            // RIGHT: offer image
            if (offerB64) {
                doc.setDrawColor(...PC);
                doc.setLineWidth(0.5);
                doc.addImage(offerB64, 'JPEG', rightX, detailsY, imgW, imgH);
                doc.setDrawColor(220, 210, 200);
                doc.roundedRect(rightX, detailsY, imgW, imgH, 2, 2);
            }

            // ── 3. PRICING BREAKDOWN ──────────────────────────────────────
            let tableY = Math.max(ly + 6, detailsY + imgH + 8);

            // Table header
            doc.setFillColor(...TC);
            doc.rect(leftX, tableY, PW - leftX * 2, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(255, 255, 255);
            doc.text('Description',  leftX + 2,      tableY + 5.5);
            doc.text('Qty',          leftX + 95,     tableY + 5.5);
            doc.text('Unit Price',   leftX + 112,    tableY + 5.5);
            doc.text('Amount',       PW - leftX - 18, tableY + 5.5);
            tableY += 8;

            // Table rows
            const items = booking?.billItems || [];
            const baseItems = items.filter(i => !i.billing || i.billing === 'BASE');
            const allItems  = baseItems.length > 0 ? baseItems : items;

            const toEur = (cents) => {
                const cur = rawBill?.currency || 'EUR';
                const v = (Number(cents) / 100).toFixed(2);
                return `${cur} ${v}`;
            };

            let subtotalCents = 0;
            allItems.forEach((item, i) => {
                const rowY   = tableY + i * 7;
                const isEven = i % 2 === 0;
                doc.setFillColor(...(isEven ? PC : [255, 255, 255]));
                doc.rect(leftX, rowY, PW - leftX * 2, 7, 'F');

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8.5);
                doc.setTextColor(...WN);
                const desc  = item.category || item.description || 'Item';
                doc.text(doc.splitTextToSize(desc, 90)[0], leftX + 2, rowY + 5);
                doc.text(String(item.quantity || 1),   leftX + 95,      rowY + 5);
                doc.text(toEur(item.unitPrice || 0),   leftX + 112,     rowY + 5);
                doc.setFont('helvetica', 'bold');
                doc.text(toEur(item.amount || 0), PW - leftX - 2, rowY + 5, { align: 'right' });
                subtotalCents += Number(item.amount || 0);
            });

            tableY += allItems.length * 7 + 2;

            // Separator
            doc.setDrawColor(...TC);
            doc.setLineWidth(0.4);
            doc.line(leftX, tableY, PW - leftX, tableY);
            tableY += 5;

            // For RESERVED BikeTravelBill show deposit paid / remaining
            const isReservedBill = rawBill?.billStatus === 'RESERVED';
            if (isReservedBill && rawBill?.depositAmount) {
                const dep = Number(rawBill.depositAmount);
                const rem = Number(rawBill.totalAmount) - dep;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8.5);
                doc.setTextColor(...GR);
                doc.text('30% Deposit Paid:', leftX + 2, tableY);
                doc.setTextColor(74, 103, 65);
                doc.text(toEur(dep), PW - leftX - 2, tableY, { align: 'right' });
                tableY += 6;
                doc.setTextColor(...GR);
                doc.text('Remaining Balance:', leftX + 2, tableY);
                doc.setTextColor(196, 98, 45);
                doc.text(toEur(rem), PW - leftX - 2, tableY, { align: 'right' });
                tableY += 6;
                doc.setDrawColor(...GD);
                doc.line(leftX, tableY, PW - leftX, tableY);
                tableY += 5;
            }

            // Total row
            doc.setFillColor(...WN);
            doc.rect(leftX, tableY, PW - leftX * 2, 9, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...GD);
            doc.text('TOTAL', leftX + 2, tableY + 6.3);
            const totalAmt = rawBill?.totalAmount != null
                ? toEur(rawBill.totalAmount)
                : toEur(subtotalCents);
            doc.text(totalAmt, PW - leftX - 2, tableY + 6.3, { align: 'right' });
            tableY += 14;

            // ── 4. FOOTER ─────────────────────────────────────────────────
            const footerY = PH - 18;
            doc.setDrawColor(...GD);
            doc.setLineWidth(0.3);
            doc.line(leftX, footerY, PW - leftX, footerY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(...GR);
            doc.text('haytour.es  ·  Sevi Tours  ·  Armenia', PW / 2, footerY + 5, { align: 'center' });
            doc.setTextColor(...TC);
            doc.text('Thank you for travelling with us', PW / 2, footerY + 10, { align: 'center' });

            doc.save(`haytour-invoice-${id}.pdf`);
        } catch (err) {
            alert('Could not generate invoice: ' + err.message);
        }
    };

    if (loading) return <BookingDetailsSkeleton />;

    if (!booking) return (
        <Container sx={{ py: 10, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{t('booking.not_found')}</Typography>
            <Button component="a" href="/my-bookings" sx={{ mt: 2 }}>{t('booking.my_bookings')}</Button>
        </Container>
    );

    // ── Derived values ────────────────────────────────────────────────────────
    // Helper: format a cents value, converting from the bill's currency to the user's selected currency
    const fmtBill = (cents) => format(cents, booking.currency || 'EUR');

    const STATUS_CONFIG = {
        DRAFT:            { color: 'warning', label: t('booking.status_pending')       },
        PAID:             { color: 'success', label: t('booking.status_confirmed')     },
        RESERVED:         { color: 'info',    label: t('booking.status_reserved', 'Reserved — 30% paid') },
        CANCELED:         { color: 'error',   label: t('booking.status_canceled')      },
        DELETED:          { color: 'error',   label: t('booking.status_hidden')        },
        REFUNDED:         { color: 'info',    label: t('booking.status_refunded')      },
        REFUND_REQUESTED: { color: 'warning', label: t('booking.status_refund_pending')},
    };
    const statusCfg       = STATUS_CONFIG[booking.billStatus] || STATUS_CONFIG.CANCELED;
    const isPaid          = booking.billStatus === 'PAID';
    const isDraft         = booking.billStatus === 'DRAFT';
    const isReservedBill  = booking.billStatus === 'RESERVED';
    const isCanceled      = ['CANCELED', 'DELETED'].includes(booking.billStatus);
    const isRefundPending = booking.billStatus === 'REFUND_REQUESTED';
    const canCancel       = !isManager && ['DRAFT', 'PAID', 'RESERVED'].includes(booking.billStatus);

    const isBikeTravel = rawBill?.billType === 'BIKE_TRAVEL';

    // Customer or manager can edit on DRAFT; manager can also edit on PAID (TOUR only — RENTAL handled separately)
    const canEditBooking = !isBikeTravel && booking.sourceType !== 'RENTAL' && (isDraft || (isManager && isPaid));
    const canEditRental  = !isBikeTravel && booking.sourceType === 'RENTAL'  && (isDraft || (isManager && isPaid));
    const canEditDate    = booking.sourceType !== 'RENTAL' && ((!isManager && isDraft) || (isManager && isPaid));

    const totalPeopleCount  = ALL_CATEGORIES.reduce((sum, key) => sum + (editCounts[key]?.quantity || 0), 0);
    const canEditNotes      = isDraft;
    const canEditRentalDate = booking.sourceType === 'RENTAL' && isDraft;

    // Slot helpers for the date popover
    const tourActiveDays = offerSlots.length > 0
        ? offerSlots.reduce((acc, s) => acc | (s.activeDays ?? 127), 0)
        : null;
    const slotsForDate = (date) => {
        if (!date || offerSlots.length === 0) return [];
        const bit = JS_TO_BIT[date.getDay()];
        return offerSlots.filter(s => ((s.activeDays ?? 127) & (1 << bit)) !== 0);
    };
    const availableSlots = slotsForDate(editDate);

    // Bill item groups
    const baseItems     = booking.billItems.filter(i => !i.billing || i.billing === 'BASE');
    const extraItems    = booking.billItems.filter(i => i.billing === 'EXTRA');
    const taxItems      = booking.billItems.filter(i => i.billing === 'TAX');
    const discountItems = booking.billItems.filter(i => i.billing === 'DISCOUNT');

    const fmtDateTime = (iso) => iso
        ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
        : 'N/A';

    const col1Info = isBikeTravel ? [
        { icon: <PersonIcon        sx={{ fontSize: '1rem' }} />, label: t('booking.booking_ref'), value: `#${booking.id.toString().split('-')[0]}` },
        { icon: <CalendarMonthIcon sx={{ fontSize: '1rem' }} />, label: 'Start date',              value: rawBill?.startDate || 'N/A' },
    ] : booking.sourceType === 'RENTAL' ? [
        { icon: <PersonIcon       sx={{ fontSize: '1rem' }} />, label: t('booking.booking_ref'), value: `#${booking.id.toString().split('-')[0]}` },
        {
            icon: <CalendarMonthIcon sx={{ fontSize: '1rem' }} />, label: 'Start',
            value: fmtDateTime(booking.rentalStartTime),
            action: canEditRentalDate ? (
                <IconButton size="small" onClick={() => setRentalDateOpen(true)}
                            sx={{ p: 0.2, color: 'primary.main', ml: 0.3 }}>
                    <EditOutlinedIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
            ) : null,
        },
    ] : [
        { icon: <PersonIcon     sx={{ fontSize: '1rem' }} />, label: t('booking.booking_ref'), value: `#${booking.id.toString().split('-')[0]}` },
        { icon: <AccessTimeIcon sx={{ fontSize: '1rem' }} />, label: t('booking.duration'),    value: booking.duration || 'N/A' },
    ];

    const col2Info = isBikeTravel ? [
        { icon: <PersonIcon     sx={{ fontSize: '1rem' }} />, label: 'Participants', value: String(rawBill?.participantCount || '—') },
        { icon: <PaymentIcon    sx={{ fontSize: '1rem' }} />, label: 'Payment type', value: rawBill?.paymentType === 'RESERVE' ? '30% deposit + balance' : 'Full payment' },
    ] : booking.sourceType === 'RENTAL' ? [
        { icon: <AccessTimeIcon sx={{ fontSize: '1rem' }} />, label: 'End',      value: fmtDateTime(booking.rentalEndTime) },
        { icon: <LocationOnIcon sx={{ fontSize: '1rem' }} />, label: 'District', value: booking.districtAddress || 'N/A' },
    ] : [
        {
            icon:  <CalendarMonthIcon sx={{ fontSize: '1rem' }} />,
            label: t('booking.date'),
            value: booking.tourDate
                ? new Date(booking.tourDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                : 'N/A',
            action: canEditDate ? (
                <IconButton size="small" onClick={handleOpenCalendar}
                            sx={{ p: 0.2, color: 'primary.main', ml: 0.3 }}>
                    <EditOutlinedIcon sx={{ fontSize: '0.85rem' }} />
                </IconButton>
            ) : null,
        },
        {
            icon:  <PhoneIcon sx={{ fontSize: '1rem' }} />,
            label: t('booking.time'),
            value: booking.startTime
                ? `${booking.startTime}${booking.endTime ? ` – ${booking.endTime}` : ''}`
                : 'N/A',
        },
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("${booking.tourImage}") center/cover fixed` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
            {isSuccess && (
                <Alert severity="success" sx={{ mb: 4, fontWeight: 600 }}>
                    {t('booking.confirmed')}
                </Alert>
            )}
            {isReserved && (
                <Alert severity="info" sx={{ mb: 4, fontWeight: 600 }}>
                    Your 30% deposit has been received. Your booking is now reserved!
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* ── ROW 1: Tour Info + Timeline ── */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch', flexDirection: { xs: 'column', lg: 'row' } }}>

                    {/* Tour Info */}
                    <Box sx={{ flex: { xs: 'none', lg: 7 }, minWidth: 0, display: 'flex' }}>
                        <Card elevation={2} sx={{
                            width: '100%', display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            overflow: 'hidden',
                        }}>
                            {/* Landscape image panel */}
                            <Box sx={{
                                flexShrink: 0, position: 'relative',
                                width: { xs: '100%', sm: 300 },
                                height: { xs: 180, sm: 'auto' },
                                minHeight: { sm: 180 },
                                backgroundImage: `url(${booking.tourImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}>
                                <Box sx={{ position: 'absolute', inset: 0,
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)' }} />
                                <Chip label={statusCfg.label} color={statusCfg.color}
                                      sx={{ position: 'absolute', top: 12, left: 12, fontWeight: 700 }} />
                            </Box>

                            {/* Content: title → image fill → 2-col info */}
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 3, pb: 1.5 }}>
                                    <Typography variant="h6" fontWeight="bold">{booking.tourName}</Typography>
                                    {isManager && booking.customerName && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, p: 1.5,
                                            bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 2 }}>
                                            <Avatar src={booking.customerImageUrl} alt={booking.customerName}
                                                    sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.light' }}>
                                                {booking.customerName[0].toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{booking.customerName}</Typography>
                                                {booking.customerEmail && <Typography variant="caption" color="text.secondary">{booking.customerEmail}</Typography>}
                                                {booking.customerPhone && <Typography variant="caption" color="text.secondary" display="block">{booking.customerPhone}</Typography>}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{
                                    flexGrow: 1,
                                    backgroundImage: `url(${booking.tourImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                }} />

                                <Box sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {col1Info.map(({ icon, label, value, action }) => (
                                                    <Box key={label} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                        <Box sx={{ color: 'primary.main', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>{label}</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Typography variant="body2" fontWeight={600}>{value}</Typography>
                                                            {action}</Box>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {col2Info.map(({ icon, label, value, action }) => (
                                                    <Box key={label} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                        <Box sx={{ color: 'primary.main', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>{label}</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Typography variant="body2" fontWeight={600}>{value}</Typography>
                                                                {action}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Card>
                    </Box>

                    {/* Booking Timeline */}
                    <Box sx={{ flex: { xs: 'none', lg: 5 }, minWidth: 0, display: 'flex' }}>
                        <Card elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>{t('booking.timeline')}</Typography>
                                <Stepper orientation="vertical" sx={{ mt: 1 }}>
                                    {booking.timeline.map((step, i) => (
                                        <Step key={i} completed={step.completed}>
                                            <StepLabel
                                                optional={step.completed && step.date ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(step.date).toLocaleDateString('en-GB',
                                                            { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </Typography>
                                                ) : null}
                                            >
                                                <Typography variant="body2" fontWeight={600}
                                                            color={step.completed ? 'text.primary' : 'text.disabled'}>
                                                    {step.label}
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </CardContent>
                            <Box sx={{
                                flexGrow: 1,
                                backgroundImage: `url(${booking.tourImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }} />
                        </Card>
                    </Box>
                </Box>

                {/* ── ROW 2: Pricing Breakdown ── */}
                <Card elevation={2}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {t('booking.pricing')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {/* People categories — editable (+/−) or read-only */}

                        {/* BIKE_TRAVEL: simplified single-row layout */}
                        {isBikeTravel && (
                            <Box>
                                {booking.billStatus === 'RESERVED' ? (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 1.5,
                                            bgcolor: 'rgba(201,151,58,0.07)', borderRadius: 1, mb: 0.5 }}>
                                            <Typography variant="body1" sx={{ flex: 1 }}>30% Deposit paid</Typography>
                                            <Typography variant="body1" fontWeight={700}>
                                                {fmtBill(booking.depositAmount)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 1.5,
                                            bgcolor: 'rgba(74,103,65,0.06)', borderRadius: 1, mb: 0.5 }}>
                                            <Typography variant="body1" sx={{ flex: 1 }}>Balance due (70%)</Typography>
                                            <Typography variant="body1" fontWeight={700}>
                                                {fmtBill(booking.totalPrice - booking.depositAmount)}
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 1.5,
                                        bgcolor: 'rgba(74,103,65,0.06)', borderRadius: 1, mb: 0.5 }}>
                                        <Typography variant="body1" sx={{ flex: 1 }}>Total paid</Typography>
                                        <Typography variant="body1" fontWeight={700}>
                                            {fmtBill(booking.totalPrice)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* RENTAL edit: per-bike-type steppers (all 5 rows always shown) */}
                        {canEditRental && (
                            <Box>
                                {/* Header row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 1.5, mb: 0.5 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: '1 1 120px', minWidth: 0 }}>Bike Type</Typography>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 90, textAlign: 'center' }}>Qty</Typography>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>Total</Typography>
                                </Box>
                                <Divider sx={{ mb: 0.5, borderStyle: 'dashed' }} />
                                {ALL_BIKE_TYPES.map(({ key, label }, i) => {
                                    const item = rentalEditCounts[key] || { quantity: 0, unitPrice: 0, itemId: null };
                                    const { quantity = 0, unitPrice = 0 } = item;
                                    return (
                                        <Box key={key} sx={{
                                            display: 'flex', alignItems: 'center',
                                            bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                            py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                        }}>
                                            <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }}>
                                                {label}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: 90, justifyContent: 'center' }}>
                                                <IconButton size="small" onClick={() => handleRentalCountChange(key, -1)}
                                                            disabled={quantity === 0} sx={{ color: 'primary.main', p: 0.3 }}>
                                                    <RemoveCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                                <Typography variant="body2" fontWeight="bold"
                                                            sx={{ minWidth: 22, textAlign: 'center' }}>
                                                    {quantity}
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleRentalCountChange(key, 1)}
                                                            sx={{ color: 'primary.main', p: 0.3 }}>
                                                    <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                                                {fmtBill(quantity * unitPrice)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                                    <Typography variant="body1" fontWeight={700}>
                                        {fmtBill(ALL_BIKE_TYPES.reduce((s, { key }) => s + (rentalEditCounts[key]?.quantity || 0) * (rentalEditCounts[key]?.unitPrice || 0), 0))}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Total:</Typography>
                                    <Typography variant="body1" fontWeight={900} color="primary">
                                        {fmtBill(
                                            ALL_BIKE_TYPES.reduce((s, { key }) => s + (rentalEditCounts[key]?.quantity || 0) * (rentalEditCounts[key]?.unitPrice || 0), 0)
                                            + Object.values(addonEditCounts).reduce((s, { qty, unitPrice }) => s + (qty || 0) * (unitPrice || 0), 0)
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="contained" color="primary"
                                            startIcon={savingRental ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={handleSaveRentalCounts} disabled={savingRental}>
                                        {savingRental ? 'Saving…' : 'Save Changes'}
                                    </Button>
                                </Box>

                                {/* Addons section (DRAFT only) */}
                                {isDraft && addonConfigs.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Add-ons</Typography>
                                        {addonConfigs.map((ac, i) => {
                                            const meta = ADDON_META[ac.equipmentType] || { label: ac.equipmentType };
                                            const { qty = 0, unitPrice = Number(ac.pricePerUnit || 0) } = addonEditCounts[ac.equipmentType] || {};
                                            return (
                                                <Box key={ac.equipmentType} sx={{
                                                    display: 'flex', alignItems: 'center',
                                                    bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                                    py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                                }}>
                                                    <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }}>
                                                        {meta.label} ({fmtBill(unitPrice)})
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: 90, justifyContent: 'center' }}>
                                                        <IconButton size="small" onClick={() => handleAddonCountChange(ac.equipmentType, -1)}
                                                                    disabled={qty === 0} sx={{ color: 'primary.main', p: 0.3 }}>
                                                            <RemoveCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 22, textAlign: 'center' }}>{qty}</Typography>
                                                        <IconButton size="small" onClick={() => handleAddonCountChange(ac.equipmentType, 1)}
                                                                    disabled={qty >= totalBikeCount}
                                                                    sx={{ color: 'primary.main', p: 0.3 }}>
                                                            <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                        </IconButton>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                                                        {fmtBill(qty * unitPrice)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button size="small" variant="outlined" color="primary"
                                                    startIcon={savingAddons ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                                                    onClick={handleSaveAddons} disabled={savingAddons}>
                                                {savingAddons ? 'Saving…' : 'Save Add-ons'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* RENTAL read-only: bikes + addons table */}
                        {!isBikeTravel && booking.sourceType === 'RENTAL' && !canEditRental && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 1.5, mb: 0.5 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: '1 1 120px', minWidth: 0 }}>Item</Typography>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 90, textAlign: 'center' }}>Qty</Typography>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>Total</Typography>
                                </Box>
                                <Divider sx={{ mb: 0.5, borderStyle: 'dashed' }} />
                                {ALL_BIKE_TYPES.filter(({ key }) => (rentalEditCounts[key]?.quantity || 0) > 0).map(({ key, label }, i) => {
                                    const { quantity: qty = 0, unitPrice: up = 0 } = rentalEditCounts[key] || {};
                                    return (
                                        <Box key={key} sx={{
                                            display: 'flex', alignItems: 'center',
                                            bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                            py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                        }}>
                                            <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }}>{label}</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 90, textAlign: 'center' }}>{qty}</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                                                {fmtBill(qty * up)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {/* Addons in read-only */}
                                {Object.entries(addonEditCounts).filter(([, { qty }]) => qty > 0).length > 0 && (
                                    <>
                                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ px: 1.5, mb: 0.5, display: 'block' }}>Add-ons</Typography>
                                        {Object.entries(addonEditCounts).filter(([, { qty }]) => qty > 0).map(([et, { qty, unitPrice }], i) => {
                                            const meta = ADDON_META[et] || { label: et };
                                            return (
                                                <Box key={et} sx={{
                                                    display: 'flex', alignItems: 'center',
                                                    bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                                    py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                                }}>
                                                    <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }}>{meta.label}</Typography>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 90, textAlign: 'center' }}>{qty}</Typography>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                                                        {fmtBill(qty * unitPrice)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </>
                                )}
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight={700}>Total:</Typography>
                                    <Typography variant="body1" fontWeight={900} color="primary">{fmtBill(booking.totalPrice)}</Typography>
                                </Box>
                            </Box>
                        )}

                        {/* TOUR edit: BIKE = 2-column Normal/E-Bike per category */}
                        {canEditBooking && booking.tourType === 'BIKE' && (
                            <Box>
                                {/* Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 1.5, mb: 0.5 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: '1 1 120px', minWidth: 0 }}>Category</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mx: 1 }}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 80, textAlign: 'center' }}>Normal</Typography>
                                        <ElectricBikeIcon sx={{ fontSize: '0.85rem', color: '#4A6741' }} />
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 80, textAlign: 'center' }}>E-Bike</Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>Row Total</Typography>
                                </Box>
                                <Divider sx={{ mb: 0.5, borderStyle: 'dashed' }} />
                                {ALL_CATEGORIES.map((key, i) => {
                                    const { quantity: nQty = 0, unitPrice: nPrice = 0 } = editCounts[key] || {};
                                    const { quantity: eQty = 0, unitPrice: ePrice = 0 } = editEBikeCounts[key] || {};
                                    const rowTotal = nQty * nPrice + eQty * ePrice;
                                    return (
                                        <Box key={key} sx={{
                                            display: 'flex', alignItems: 'center',
                                            bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                            py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                        }}>
                                            <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0, wordBreak: 'break-word' }}>
                                                {key}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mx: 1 }}>
                                                {/* Normal stepper */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 80, justifyContent: 'center' }}>
                                                    <IconButton size="small" onClick={() => handleCountChange(key, -1)}
                                                                disabled={nQty === 0} sx={{ color: 'primary.main', p: 0.3 }}>
                                                        <RemoveCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 22, textAlign: 'center' }}>
                                                        {nQty}
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => handleCountChange(key, 1)}
                                                                sx={{ color: 'primary.main', p: 0.3 }}>
                                                        <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                </Box>
                                                {/* E-Bike stepper */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 80, justifyContent: 'center' }}>
                                                    <IconButton size="small" onClick={() => handleEBikeCountChange(key, -1)}
                                                                disabled={eQty === 0} sx={{ color: '#4A6741', p: 0.3 }}>
                                                        <RemoveCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 22, textAlign: 'center', color: eQty > 0 ? '#4A6741' : 'text.secondary' }}>
                                                        {eQty}
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => handleEBikeCountChange(key, 1)}
                                                                sx={{ color: '#4A6741', p: 0.3 }}>
                                                        <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                                                {fmtBill(rowTotal)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {/* Live subtotal */}
                                {(() => {
                                    const previewTotal = ALL_CATEGORIES.reduce((s, k) =>
                                        s + (editCounts[k]?.quantity || 0) * (editCounts[k]?.unitPrice || 0)
                                          + (editEBikeCounts[k]?.quantity || 0) * (editEBikeCounts[k]?.unitPrice || 0), 0);
                                    return (
                                        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                                            <Typography variant="body1" fontWeight={700}>{fmtBill(previewTotal)}</Typography>
                                        </Box>
                                    );
                                })()}
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="contained" color="primary"
                                            startIcon={savingCounts ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={handleSaveCounts} disabled={savingCounts}>
                                        {savingCounts ? 'Saving…' : 'Save Changes'}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* TOUR edit: non-BIKE flat category list */}
                        {canEditBooking && booking.tourType !== 'BIKE' && (
                            <Box>
                                {ALL_CATEGORIES.map((key, i) => {
                                    const { quantity = 0, unitPrice = 0 } = editCounts[key] || {};
                                    return (
                                        <Box key={key} sx={{
                                            display: 'flex', alignItems: 'center',
                                            bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                            py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                        }}>
                                            <Typography variant="body1" sx={{ flex: '1 1 120px', minWidth: 0, wordBreak: 'break-word' }}>
                                                {key} ({fmtBill(unitPrice)})
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mx: 1 }}>
                                                <IconButton size="small" onClick={() => handleCountChange(key, -1)}
                                                            disabled={quantity === 0} sx={{ color: 'primary.main' }}>
                                                    <RemoveCircleOutlineIcon fontSize="small" />
                                                </IconButton>
                                                <Typography variant="body1" fontWeight="bold"
                                                            sx={{ minWidth: 28, textAlign: 'center' }}>
                                                    {quantity}
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleCountChange(key, 1)}
                                                            sx={{ color: 'primary.main' }}>
                                                    <AddCircleOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ flexGrow: 1, mx: 2, borderBottom: '2px dotted',
                                                borderColor: 'text.secondary', opacity: 0.3,
                                                position: 'relative', top: '4px' }} />
                                            <Typography variant="body1" fontWeight="bold" sx={{ flexShrink: 0 }}>
                                                {fmtBill(quantity * unitPrice)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {/* Electric bike upgrade row (non-BIKE tour) */}
                                {electricBikeEdit.unitPrice > 0 && (
                                    <>
                                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center',
                                            bgcolor: 'rgba(74,103,65,0.05)',
                                            py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                        }}>
                                            <ElectricBikeIcon sx={{ fontSize: '1rem', color: '#4A6741', mr: 0.8, flexShrink: 0 }} />
                                            <Typography variant="body1" sx={{ flex: '1 1 120px', minWidth: 0, wordBreak: 'break-word' }}>
                                                Electric bike upgrade ({fmtBill(electricBikeEdit.unitPrice)})
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mx: 1 }}>
                                                <IconButton size="small"
                                                    onClick={() => setElectricBikeEdit(prev => ({ ...prev, quantity: Math.max(0, prev.quantity - 1) }))}
                                                    disabled={electricBikeEdit.quantity === 0}
                                                    sx={{ color: 'primary.main' }}>
                                                    <RemoveCircleOutlineIcon fontSize="small" />
                                                </IconButton>
                                                <Typography variant="body1" fontWeight="bold"
                                                    sx={{ minWidth: 28, textAlign: 'center' }}>
                                                    {electricBikeEdit.quantity}
                                                </Typography>
                                                <IconButton size="small"
                                                    onClick={() => setElectricBikeEdit(prev => ({ ...prev, quantity: Math.min(totalPeopleCount, prev.quantity + 1) }))}
                                                    disabled={electricBikeEdit.quantity >= totalPeopleCount}
                                                    sx={{ color: 'primary.main' }}>
                                                    <AddCircleOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ flexGrow: 1, mx: 2, borderBottom: '2px dotted',
                                                borderColor: 'text.secondary', opacity: 0.3,
                                                position: 'relative', top: '4px' }} />
                                            <Typography variant="body1" fontWeight="bold" sx={{ flexShrink: 0 }}>
                                                {fmtBill(electricBikeEdit.quantity * electricBikeEdit.unitPrice)}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                                {/* Live running total */}
                                {(() => {
                                    const previewTotal = ALL_CATEGORIES.reduce(
                                        (s, k) => s + (editCounts[k]?.quantity || 0) * (editCounts[k]?.unitPrice || 0), 0
                                    ) + electricBikeEdit.quantity * electricBikeEdit.unitPrice;
                                    return (
                                        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                                            <Typography variant="body1" fontWeight={700}>{fmtBill(previewTotal)}</Typography>
                                        </Box>
                                    );
                                })()}
                                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="contained" color="primary"
                                            startIcon={savingCounts
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <SaveIcon />}
                                            onClick={handleSaveCounts} disabled={savingCounts}>
                                        {savingCounts ? 'Saving…' : 'Save Changes'}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Read-only pricing (only when not in any edit mode, not BIKE_TRAVEL, not RENTAL) */}
                        {!isBikeTravel && !canEditBooking && !canEditRental && booking.sourceType !== 'RENTAL' && (booking.tourType === 'BIKE' && baseItems.some(i => (i.category || '').endsWith(' ELECTRIC')) ? (
                            // ── BIKE tour: 3-column grouped table ──
                            <Box sx={{ width: '100%' }}>
                                {/* Header row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, px: 1.5, mb: 0.5 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: '1 1 140px', minWidth: 0 }}>Category</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 72, textAlign: 'center' }}>Normal</Typography>
                                        <ElectricBikeIcon sx={{ fontSize: '0.85rem', color: '#4A6741' }} />
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 72, textAlign: 'center' }}>E-Bike</Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flexShrink: 0, minWidth: 60, textAlign: 'right' }}>Total</Typography>
                                </Box>
                                <Divider sx={{ mb: 0.5, borderStyle: 'dashed' }} />
                                {(() => {
                                    const groups = {};
                                    baseItems.forEach(item => {
                                        const cat = item.category || '';
                                        const isElec = cat.endsWith(' ELECTRIC');
                                        const base = isElec ? cat.slice(0, -8).trimEnd() : cat;
                                        if (!groups[base]) groups[base] = { normal: null, electric: null };
                                        if (isElec) groups[base].electric = item;
                                        else groups[base].normal = item;
                                    });
                                    return Object.entries(groups).map(([cat, { normal, electric }], i) => {
                                        const rowTotal = (normal?.amount || 0) + (electric?.amount || 0);
                                        return (
                                            <Box key={cat} sx={{
                                                display: 'flex', alignItems: 'center',
                                                bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.100',
                                                py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                            }}>
                                                <Typography variant="body2" sx={{ flex: '1 1 140px', minWidth: 0, wordBreak: 'break-word' }}>
                                                    {cat}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                                                    <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'center', color: normal?.quantity ? 'text.primary' : 'text.disabled' }}>
                                                        {normal ? `${normal.quantity}× ${fmtBill(normal.unitPrice)}` : '—'}
                                                    </Typography>
                                                    <Box sx={{ width: 1 }} />
                                                    <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'center', color: electric?.quantity ? '#4A6741' : 'text.disabled', fontWeight: electric?.quantity ? 600 : 400 }}>
                                                        {electric ? `${electric.quantity}× ${fmtBill(electric.unitPrice)}` : '—'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0, minWidth: 60, textAlign: 'right' }}>
                                                    {fmtBill(rowTotal)}
                                                </Typography>
                                            </Box>
                                        );
                                    });
                                })()}
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%' }}>
                                {baseItems.map((item, i) => {
                                    const isEBike = (item.category || '').includes('ELECTRIC_BIKE');
                                    return (
                                        <Box key={i} sx={{
                                            display: 'flex', alignItems: 'center',
                                            bgcolor: isEBike ? 'rgba(74,103,65,0.05)' : (i % 2 === 0 ? 'background.paper' : 'grey.100'),
                                            py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                        }}>
                                            {isEBike && (
                                                <ElectricBikeIcon sx={{ fontSize: '1rem', color: '#4A6741', mr: 0.8, flexShrink: 0 }} />
                                            )}
                                            <Typography variant="body1">
                                                {item.quantity}x {isEBike ? 'Electric bike upgrade' : item.category} ({fmtBill(item.unitPrice)})
                                            </Typography>
                                            <Box sx={{ flexGrow: 1, mx: 2, borderBottom: '2px dotted',
                                                borderColor: 'text.secondary', opacity: 0.3,
                                                position: 'relative', top: '4px' }} />
                                            <Typography variant="body1" fontWeight="bold">
                                                {fmtBill(item.amount)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))}

                        {/* Extra items (add-ons) */}
                        {extraItems.length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                                {extraItems.map((item, i) => (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'center',
                                        bgcolor: (baseItems.length + i) % 2 === 0 ? 'background.paper' : 'grey.100',
                                        py: 1, px: 1.5, borderRadius: 1, mb: 0.5,
                                    }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.quantity > 1 && `${item.quantity}x `}{item.category}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1, mx: 2, borderBottom: '1px dotted #ccc',
                                            opacity: 0.4, position: 'relative', top: '4px' }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                            +{fmtBill(item.amount)}
                                        </Typography>
                                    </Box>
                                ))}
                            </>
                        )}

                        {/* Totals */}
                        <Box mt={3} pt={2} sx={{ borderTop: '1px dashed #ccc' }}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">{t('booking.subtotal')}</Typography>
                                <Box sx={{ flexGrow: 1, mx: 1, borderBottom: '1px dotted #ccc', opacity: 0.2, height: '1em' }} />
                                <Typography variant="body2" fontWeight="bold">{fmtBill(booking.subtotal)}</Typography>
                            </Box>

                            {taxItems.length > 0
                                ? taxItems.map((item, i) => (
                                    <Box key={i} display="flex" alignItems="center" mb={1}>
                                        <Typography variant="body2" color="text.secondary">{item.category || t('booking.tax')}</Typography>
                                        <Box sx={{ flexGrow: 1, mx: 1, borderBottom: '1px dotted #ccc', opacity: 0.2, height: '1em' }} />
                                        <Typography variant="body2" fontWeight="bold">+{fmtBill(item.amount)}</Typography>
                                    </Box>
                                ))
                                : booking.tax !== 0 && (
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Typography variant="body2" color="text.secondary">{t('booking.tax')}</Typography>
                                        <Box sx={{ flexGrow: 1, mx: 1, borderBottom: '1px dotted #ccc', opacity: 0.2, height: '1em' }} />
                                        <Typography variant="body2" fontWeight="bold">{fmtBill(booking.tax)}</Typography>
                                    </Box>
                                )
                            }

                            {discountItems.map((item, i) => (
                                <Box key={i} display="flex" alignItems="center" mb={1}>
                                    <Typography variant="body2" sx={{ color: 'success.main' }}>{item.category || 'Discount'}</Typography>
                                    <Box sx={{ flexGrow: 1, mx: 1, borderBottom: '1px dotted #ccc', opacity: 0.2, height: '1em' }} />
                                    <Typography variant="body2" fontWeight="bold" sx={{ color: 'success.main' }}>
                                        −{fmtBill(Math.abs(item.amount))}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {booking.sourceType === 'RENTAL' && booking.depositAmount > 0 && (
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" color="text.secondary">Security Deposit</Typography>
                                <Box sx={{ flexGrow: 1, mx: 1, borderBottom: '1px dotted #ccc', opacity: 0.2, height: '1em' }} />
                                <Typography variant="body2" fontWeight="bold" sx={{ color: TERRACOTTA }}>
                                    +{fmtBill(booking.depositAmount)}
                                </Typography>
                            </Box>
                        )}

                        <Box mt={3} pt={3} sx={{ borderTop: '2px solid #ddd' }} display="flex" alignItems="center">
                            <Typography variant="h5" fontWeight="900">{t('booking.total')}</Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <Typography variant="h4" fontWeight="900" color="primary">
                                {fmtBill(booking.totalPrice)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* ── Notes ── */}
                {(booking.notes || canEditNotes) && (
                    <Card elevation={2}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <NotesIcon sx={{ color: 'primary.main' }} /> Notes
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {canEditNotes ? (
                                <Box>
                                    <TextField
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        placeholder="Add any notes or special requests…"
                                        value={editNotes}
                                        onChange={e => setEditNotes(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                    />
                                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button variant="contained" color="primary"
                                                startIcon={savingNotes
                                                    ? <CircularProgress size={16} color="inherit" />
                                                    : <SaveIcon />}
                                                onClick={handleSaveNotes}
                                                disabled={savingNotes}>
                                            {savingNotes ? 'Saving…' : 'Save Notes'}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary"
                                            sx={{ whiteSpace: 'pre-wrap' }}>
                                    {booking.notes || '—'}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* ── ROW 3: Actions ── */}
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} useFlexGap>
                        {!isManager && (
                            <>
                                {(isDraft || (isReservedBill && isBikeTravel)) && (
                                    <Button variant="contained" color="success"
                                            startIcon={payLoading
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <PaymentIcon />}
                                            disabled={payLoading}
                                            onClick={handlePay}>
                                        {isReservedBill
                                            ? t('booking.pay_balance', 'Pay Remaining 70%')
                                            : t('booking.pay')}
                                    </Button>
                                )}
                                {isPaid && (
                                    <Button variant="outlined" color="warning"
                                            onClick={() => handleStatusUpdate('REFUND_REQUESTED')}>
                                        {t('booking.request_refund')}
                                    </Button>
                                )}
                                {isCanceled && (
                                    <>
                                        <Button variant="outlined" color="warning"
                                                onClick={() => handleStatusUpdate('REFUND_REQUESTED')}>
                                            {t('booking.request_refund')}
                                        </Button>
                                        <Button variant="outlined"
                                                component="a"
                                                href={booking.sourceType === 'RENTAL'
                                                    ? '/rentals'
                                                    : `/tours/${booking.sourceId}`}>
                                            {t('booking.rebook')}
                                        </Button>
                                        <Button variant="outlined" color="error"
                                                startIcon={<VisibilityOffIcon />}
                                                onClick={() => setHideDialog(true)}>
                                            {t('booking.hide_record')}
                                        </Button>
                                    </>
                                )}
                                {canCancel && (
                                    <Button variant="outlined" color="error"
                                            startIcon={<CancelIcon />}
                                            onClick={() => setCancelDialog(true)}>
                                        {t('booking.cancel_booking')}
                                    </Button>
                                )}
                            </>
                        )}
                        {(isPaid || isReservedBill) && (
                            <Button variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={handleDownloadInvoice}>
                                {t('booking.invoice')}
                            </Button>
                        )}
                        {isManager && (
                            <>
                                {isRefundPending && (
                                    <>
                                        <Button variant="contained" color="success"
                                                startIcon={<CheckCircleIcon />}
                                                onClick={() => setRefundDialog({ open: true, action: 'approve' })}>
                                            {t('booking.approve_refund')}
                                        </Button>
                                        <Button variant="outlined" color="error"
                                                startIcon={<CancelIcon />}
                                                onClick={() => setRefundDialog({ open: true, action: 'deny' })}>
                                            {t('booking.deny_refund')}
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button variant="text" color="inherit" startIcon={<HelpOutlineIcon />}>
                            {t('booking.support')}
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            {/* ── Rating Widget (customers only, PAID bookings) ── */}
            {!isManager && (
                <RatingWidget
                    billId={booking.id}
                    billStatus={booking.billStatus}
                    sourceType={booking.sourceType}
                    offerId={booking.sourceId}
                />
            )}

            {/* ── Date / Time edit Popover ── */}
            <Popover
                open={Boolean(calendarAnchor)}
                anchorEl={calendarAnchor}
                onClose={handleCloseCalendar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2.5, borderRadius: 2, minWidth: 280 } }}
            >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonthIcon sx={{ fontSize: '1rem', color: 'primary.main' }} /> Choose Date
                </Typography>
                <MiniCalendar
                    value={editDate}
                    onChange={(date) => { setEditDate(date); setEditSlot(null); }}
                    activeDays={tourActiveDays}
                />
                {editDate && (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: '1rem', color: 'primary.main' }} /> Choose Time
                        </Typography>
                        {availableSlots.length > 0 ? (
                            <Stack spacing={0.8}>
                                {availableSlots.map((slot, i) => (
                                    <Chip key={slot.id || i}
                                          label={`${formatTime(slot.startTime)} – ${formatTime(slot.endTime)}`}
                                          variant={editSlot?.id === slot.id ? 'filled' : 'outlined'}
                                          onClick={() => setEditSlot(slot)}
                                          sx={{
                                              bgcolor:     editSlot?.id === slot.id ? 'primary.main' : 'transparent',
                                              color:       editSlot?.id === slot.id ? 'white' : 'primary.main',
                                              borderColor: 'primary.main', fontWeight: 'bold', cursor: 'pointer',
                                              '&:hover': { bgcolor: editSlot?.id === slot.id ? 'primary.dark' : 'rgba(196,98,45,0.08)' },
                                          }}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                No time slots available for this date.
                            </Typography>
                        )}
                    </Box>
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button size="small" onClick={handleCloseCalendar}>Cancel</Button>
                    <Button size="small" variant="contained" color="primary"
                            disabled={!editDate || !editSlot || savingDate}
                            startIcon={savingDate ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveDate}>
                        {savingDate ? 'Saving…' : 'Save'}
                    </Button>
                </Stack>
            </Popover>

            {/* ── Rental Date Edit Dialog ── */}
            <Dialog open={rentalDateOpen} onClose={() => !savingRentalDate && setRentalDateOpen(false)}
                    maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon sx={{ color: 'primary.main' }} /> Change Rental Date
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Date" type="date" fullWidth size="small"
                            value={rentalEditDate}
                            onChange={e => setRentalEditDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Start time" type="time" fullWidth size="small"
                            value={rentalEditTime}
                            onChange={e => setRentalEditTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        {rentalInfo?.durationType === 'HOURLY' && (
                            <TextField
                                label="Duration (hours)" type="number" fullWidth size="small"
                                value={rentalEditHours}
                                onChange={e => setRentalEditHours(Math.max(1, parseInt(e.target.value) || 1))}
                                inputProps={{ min: 1, max: 10 }}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRentalDateOpen(false)} disabled={savingRentalDate}>Cancel</Button>
                    <Button variant="contained" color="primary"
                            disabled={!rentalEditDate || !rentalEditTime || savingRentalDate}
                            startIcon={savingRentalDate ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveRentalDate}>
                        {savingRentalDate ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Cancel Dialog ── */}
            <Dialog open={cancelDialog}
                    onClose={() => !cancelLoading && setCancelDialog(false)}
                    maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {t('booking.cancel_title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {booking.billStatus === 'PAID'
                            ? t('booking.cancel_confirm_paid', { tour: booking.tourName })
                            : t('booking.cancel_confirm_draft', { tour: booking.tourName })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCancelDialog(false)} disabled={cancelLoading}>
                        {t('booking.keep_booking')}
                    </Button>
                    <Button variant="contained" color="error" onClick={handleCancel}
                            disabled={cancelLoading}
                            startIcon={cancelLoading
                                ? <CircularProgress size={16} color="inherit" />
                                : <CancelIcon />}>
                        {cancelLoading ? t('booking.canceling') : t('booking.yes_cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Hide Dialog ── */}
            <Dialog open={hideDialog}
                    onClose={() => !hideLoading && setHideDialog(false)}
                    maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{t('booking.hide_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('booking.hide_text')}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setHideDialog(false)} disabled={hideLoading}>
                        {t('booking.keep_visible')}
                    </Button>
                    <Button variant="contained" color="warning" onClick={handleHide}
                            disabled={hideLoading}
                            startIcon={hideLoading
                                ? <CircularProgress size={16} color="inherit" />
                                : <VisibilityOffIcon />}>
                        {hideLoading ? t('booking.hiding') : t('booking.hide_action')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Refund Dialog (Manager) ── */}
            <Dialog open={refundDialog.open}
                    onClose={() => !refundLoading && setRefundDialog({ open: false, action: null })}
                    maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold',
                    color: refundDialog.action === 'approve' ? 'success.main' : 'error.main' }}>
                    {refundDialog.action === 'approve'
                        ? t('booking.refund_approve_title')
                        : t('booking.refund_deny_title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {refundDialog.action === 'approve'
                            ? t('booking.refund_approve_text', {
                                amount:   format(booking.totalPrice, booking.currency),
                                currency: navCurrency
                            })
                            : t('booking.refund_deny_text')}
                    </DialogContentText>
                    <TextField label={t('booking.refund_note_label')} multiline rows={3} fullWidth
                               value={refundNote} onChange={e => setRefundNote(e.target.value)}
                               placeholder={t('booking.refund_note_placeholder')}
                               variant="outlined" size="small" />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRefundDialog({ open: false, action: null })}
                            disabled={refundLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained"
                            color={refundDialog.action === 'approve' ? 'success' : 'error'}
                            onClick={handleRefundDecision} disabled={refundLoading}
                            startIcon={refundLoading
                                ? <CircularProgress size={16} color="inherit" />
                                : null}>
                        {refundLoading
                            ? t('booking.processing')
                            : refundDialog.action === 'approve'
                                ? t('booking.confirm_approval')
                                : t('booking.confirm_denial')}
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>
        </Box>
    );
};

export default BookingDetailsContent;
